class CookieService {
    /**
     * Writes a cookie via document.cookie.
     *
     * Security notes:
     * - `Secure` is always appended — the cookie is only sent over HTTPS.
     * - `SameSite` defaults to `Strict` to prevent CSRF; callers may override
     *   to `Lax` where cross-site GET requests are required.
     * - `HttpOnly` cannot be set by client-side JavaScript (browsers silently
     *   ignore it when written via document.cookie — HTML spec §8.3.2). It is a
     *   server-side Set-Cookie directive only. Session cookies that must be
     *   HttpOnly must be issued by the backend microservice.
     */
    setCookie(
        name: string,
        value: string,
        options?: {
            maxAgeSeconds?: number;
            path?: string;
            domain?: string;
            sameSite?: 'Strict' | 'Lax' | 'None';
        },
    ) {
        let cookie = `${name}=${encodeURIComponent(value)}`;

        if (options?.maxAgeSeconds !== undefined) cookie += `; max-age=${options.maxAgeSeconds}`;
        if (options?.path) cookie += `; path=${options.path}`;
        if (options?.domain) cookie += `; domain=${options.domain}`;

        // Always enforce Secure so the cookie is never sent over plain HTTP.
        cookie += `; Secure`;

        // Default to Strict — the most restrictive SameSite policy.
        // Callers must explicitly opt down to Lax if cross-site GET reads are needed.
        const sameSite = options?.sameSite ?? 'Strict';
        cookie += `; SameSite=${sameSite}`;

        document.cookie = cookie;
    }

    getCookie(name: string): string | null {
        const match = document.cookie.match(new RegExp('(^| )' + name + '=([^;]+)'));
        return match ? decodeURIComponent(match[2]) : null;
    }

    removeCookie(name: string, path?: string, domain?: string) {
        this.setCookie(name, '', {
            maxAgeSeconds: 0,
            path,
            domain,
        });
    }
}

export const useCookie = new CookieService();
